import { useMemo } from "react";
import type { EChartsCoreOption } from "echarts/core";
import type { SkuInventorySeries, SkuSalesDay } from "../api/generated/model";
import { fmtInt } from "../api/labels";
import { type Theme, useTheme } from "../lib/theme-store";
import { EChart, cssVar } from "./EChart";
import { ChartNotes, TOD_BUCKETS, todColors, money } from "./sku-chart-display";

// -- estimated sales per day (units as bars, value as a line, dual axis) -------
// The two move together but not proportionally: the value line diverges from the bars
// exactly when the price moved, which is the reading a units/value toggle would hide.
// Each bar is stacked by the 6h IST window the sale was observed in, so "sells overnight"
// vs "sells at the evening peak" is legible without a second chart. The stack order is
// the clock (00-06 at the base), never sorted by size: a segment has to stay in the same
// band across every day or the shape stops being readable.
//
// The API always sends four, but the field is optional in the generated client (older
// payloads predate the split): fall back to booking the whole day to no bucket rather
// than inventing a distribution, so a total is never silently misattributed.
function bucketUnits(p: SkuSalesDay): number[] {
  const b = p.units_by_bucket;
  return b && b.length === TOD_BUCKETS.length ? b : [0, 0, 0, 0];
}
export function SkuSalesChart({
  series,
  loading,
}: {
  series: SkuInventorySeries | undefined;
  loading: boolean;
}) {
  const { theme } = useTheme();
  const presentation = useMemo(() => salesPresentation(series), [series]);
  const option = useMemo<EChartsCoreOption | null>(
    () => presentation.hasSales ? salesOption(presentation.points, theme) : null,
    [presentation, theme],
  );
  if (loading) return <div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading{"\u2026"}</div>;
  if (!option) {
    return (
      <div className="muted" style={{ padding: 20, textAlign: "center" }}>
        {presentation.emptyMessage}
      </div>
    );
  }
  return (
    <>
      {presentation.notice && (
        <div className="muted" style={{ padding: "8px 12px" }}>
          {presentation.notice}
        </div>
      )}
      <EChart option={option} height={196} />
      <ChartNotes
        lead={
          <>
            Estimated from falls in cart-reported quantity between sweeps, so it
            <strong> under-counts</strong>.
          </>
        }
        notes={[
          <>
            Only consecutive observations count: a restock inside a gap hides the sales before it,
            and each store's first observation contributes nothing.
          </>,
          <>
            A fall whose previous observation is over 6h back is dropped rather than booked on the
            later day, so days after a shaded gap are not inflated.
          </>,
          <>
            Bars are split by the 6h IST window the fall was <em>observed</em> in, so the split is
            only as sharp as the sweep cadence.
          </>,
          <>Today's bar is partial, "so far today".</>,
          <>Purchase limits cannot establish sales; days without usable quantities appear as gaps.</>,
        ]}
      />
    </>
  );
}

// The IST day "today" is, matching the server-side bucketing (Asia/Kolkata), so the
// open day can be drawn as partial instead of reading as a sales collapse.
function istToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
}

// A day the API omits is a day with no usable observation, not a day nothing sold: the
// API emits a row (possibly 0 units) for every day some store reported a quantity. Omission
// therefore means no sweep, or none that reported one, or the SKU was delisted -- from
// the payload the three are indistinguishable, which is why the gap is labelled "no
// data" and not "no sweeps". On a category axis a missing day and a zero day are also
// indistinguishable -- an outage silently closes up and the day after it looks like a
// spike. Materialise the missing days so the axis keeps calendar spacing and the gap can
// be drawn as a gap.
type SalesPoint = SkuSalesDay & {
  state: "complete" | "partial" | "unavailable" | "missing";
  gap: boolean;
};

// Interpret evidence once. Visibility, messages and chart mechanics consume this
// presentation rather than independently interpreting the transport flags.
function salesPresentation(series: SkuInventorySeries | undefined) {
  const points = withGaps(series?.days ?? []);
  const latest = points.at(-1);
  let emptyMessage = "No decreases observed in this window.";
  if (points.some((point) => point.state === "unavailable")) {
    emptyMessage = "Sales estimate unavailable: available stores did not report measured stock quantities.";
  } else if (points.some((point) => point.state === "partial")) {
    emptyMessage = "Partial sales estimate: no measured decreases observed; some available stores lack measured stock.";
  } else if ((series?.stores_with_quantity ?? 0) === 0) {
    emptyMessage = "This platform does not report an inventory quantity for this SKU.";
  } else if (points.length === 0 && (series?.runs.length ?? 0) > 0) {
    // Runs exist but their daily rollup has not supplied any sales observations.
    emptyMessage = "This window has not been rolled up yet, so no sales can be shown for it.";
  }
  let notice: string | undefined;
  if (latest?.state === "unavailable") {
    notice = `Sales estimate unavailable for ${latest.day}: exact stock quantities were not reported.`;
  } else if (latest?.state === "partial") {
    notice = `Partial estimate for ${latest.day}: some available stores lack measured stock.`;
  }
  return {
    points,
    hasSales: points.some((point) => !point.gap && point.units > 0),
    emptyMessage,
    notice,
  };
}

function nextDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function withGaps(points: SkuSalesDay[]): SalesPoint[] {
  if (points.length === 0) return [];
  const last = points[points.length - 1].day;
  const out: SalesPoint[] = [];
  let cursor = points[0].day;
  let i = 0;
  while (cursor <= last) {
    if (points[i]?.day === cursor) {
      const point = points[i++];
      const state = point.estimate_available === false
        ? "unavailable"
        : point.estimate_partial ? "partial" : "complete";
      out.push({ ...point, state, gap: state === "unavailable" });
    }
    else
      out.push({
        day: cursor,
        units: 0,
        value: 0,
        stores: 0,
        units_by_bucket: [0, 0, 0, 0],
        value_by_bucket: [0, 0, 0, 0],
        state: "missing",
        gap: true,
      });
    cursor = nextDay(cursor);
  }
  return out;
}

// Theme invalidates resolved CSS tokens even though it supplies no chart values.
function salesOption(points: SalesPoint[], _theme: Theme): EChartsCoreOption {
  const ink2 = cssVar("--ink-2", "#444");
  const faint = cssVar("--ink-3", "#888");
  const line = cssVar("--line", "#e5e5e5");
  const info = cssVar("--info", "#2f6feb");
  const bg = cssVar("--panel", "#fff");
  const mono = cssVar("--f-mono", "ui-monospace, monospace");
  const colors = todColors();
  const today = istToday();
  const partialAt = points.findIndex((p) => p.day === today);
  const label = (p: SalesPoint) => p.day.slice(5);
  // Contiguous runs of no-data days, as [from, to] category pairs for one shaded band
  // each -- a run of 3 dead days should read as one outage, not three stripes.
  const gapBands: { xAxis: string }[][] = [];
  for (let i = 0; i < points.length; i++) {
    if (!points[i].gap) continue;
    let j = i;
    while (j + 1 < points.length && points[j + 1].gap) j++;
    gapBands.push([{ xAxis: label(points[i]) }, { xAxis: label(points[j]) }]);
    i = j;
  }

  return {
    grid: { left: 8, right: 34, top: 26, bottom: 6, containLabel: true },
    legend: {
      show: true,
      top: 0,
      right: 4,
      itemWidth: 10,
      itemHeight: 8,
      textStyle: { color: faint, fontSize: 10, fontFamily: mono },
    },
    tooltip: {
      trigger: "axis",
      // the panel clips its overflow, so a tall tooltip near the top gets its head cut
      // off unless it is rendered on <body> instead of inside the chart container
      appendToBody: true,
      backgroundColor: bg,
      borderColor: line,
      borderWidth: 1,
      padding: [6, 9],
      textStyle: { color: ink2, fontSize: 11, fontFamily: mono },
      // a shadow, not a crosshair line: the hit target is the whole stacked column
      axisPointer: {
        type: "shadow",
        shadowStyle: { color: line, opacity: 0.35 },
      },
      formatter: (raw: unknown) => {
        const arr = raw as { dataIndex: number }[];
        const p = points[arr[0]?.dataIndex];
        if (!p) return "";
        if (p.gap) {
          return [
            p.state === "unavailable" ? "<b>Sales estimate unavailable</b>" : "<b>no data</b>",
            p.state === "unavailable"
              ? "<span style=\"opacity:.7\">availability was reported without measured stock quantities</span>"
              : "<span style=\"opacity:.7\">no store reported a quantity, so nothing estimated</span>",
            `<span style="opacity:.7">${p.day}</span>`,
          ].join("<br/>");
        }
        const units = bucketUnits(p);
        // every window is listed, zeros included: a missing row would read as "no sweep
        // covered that window" when it means "swept, nothing sold"
        const split = TOD_BUCKETS.map(
          (b, i) =>
            `<span style="opacity:.7"><span style="display:inline-block;width:7px;height:7px;` +
            `border-radius:2px;background:${colors[i]}"></span> ${b.label} ` +
            `${fmtInt(units[i])}</span>`,
        );
        return [
          `<b>${fmtInt(p.units)}</b> units sold`,
          p.state === "partial"
            ? "<span style=\"opacity:.7\">Partial estimate: some available stores lack measured stock</span>"
            : "",
          ...split,
          `<span style="opacity:.7">${money(p.value)} estimated value</span>`,
          `<span style="opacity:.7">${fmtInt(p.stores)} contributing stores</span>`,
          `<span style="opacity:.7">${p.day}${p.day === today ? " \u00b7 so far today" : ""}</span>`,
        ].join("<br/>");
      },
    },
    xAxis: {
      type: "category",
      data: points.map(label),
      axisLine: { lineStyle: { color: line } },
      axisTick: { show: false },
      axisLabel: { color: faint, fontSize: 9, fontFamily: mono },
    },
    yAxis: [
      // no axis names: the legend already names every series, and a right-hand name sits
      // exactly where the legend draws. The units axis is neutral ink because it now
      // measures four colours, not one; only the value axis still belongs to one series.
      {
        type: "value",
        axisLabel: { color: faint, fontSize: 9, fontFamily: mono },
        splitLine: { lineStyle: { color: line, type: "dashed" } },
      },
      {
        type: "value",
        axisLabel: {
          color: info,
          fontSize: 9,
          fontFamily: mono,
          formatter: (v: number) => `\u20b9${v}`,
        },
        splitLine: { show: false },
      },
    ],
    series: [
      ...TOD_BUCKETS.map((b, bi) => ({
        name: b.label,
        type: "bar",
        stack: "units",
        yAxisIndex: 0,
        // series-level colour so the LEGEND swatch is the bucket's colour: per-datum
        // itemStyle alone leaves the legend on the default palette
        color: colors[bi],
        data: points.map((p, i) =>
          // a no-data day has no bar at all -- only the shaded band below it
          p.gap
            ? null
            : {
                value: bucketUnits(p)[bi],
                // the open day is real but incomplete: draw it hollow rather than hide it
                itemStyle:
                  i === partialAt
                    ? {
                        color: colors[bi],
                        opacity: 0.3,
                        borderColor: colors[bi],
                        borderWidth: 1,
                        borderType: "dashed" as const,
                      }
                    : // a hairline of the panel surface between segments: the separator that
                      // keeps two adjacent windows legible where the hues sit closest
                      { color: colors[bi], borderColor: bg, borderWidth: 1.5 },
              },
        ),
        barMaxWidth: 22,
        // the shaded outage bands hang off the base segment only -- one band, not four
        ...(bi === 0
          ? {
              markArea: {
                silent: true,
                itemStyle: { color: line, opacity: 0.5 },
                label: { show: false },
                data: gapBands,
              },
            }
          : {}),
      })),
      {
        name: "Value (INR)",
        type: "line",
        yAxisIndex: 1,
        // the open day is left out of the solid line and redrawn dashed below, so the
        // partial value never reads as a completed day. No-data days are null too, so
        // the line breaks over an outage instead of interpolating through it.
        data: points.map((p, i) => (i === partialAt || p.gap ? null : p.value)),
        connectNulls: false,
        showSymbol: true,
        symbolSize: 5,
        smooth: false,
        lineStyle: { color: info, width: 1.75 },
        itemStyle: { color: info },
      },
      ...(partialAt > 0
        ? [
            {
              name: "Value (INR)",
              type: "line",
              yAxisIndex: 1,
              data: points.map((p, i) => (i >= partialAt - 1 && !p.gap ? p.value : null)),
              showSymbol: true,
              symbolSize: 5,
              connectNulls: false,
              lineStyle: { color: info, width: 1.75, type: "dashed" },
              itemStyle: { color: info, opacity: 0.6 },
              tooltip: { show: false },
              legendHoverLink: false,
            },
          ]
        : []),
    ],
  };
}
